import type { MetadataResponse } from '../../models/metadata-response';

/** Prefix of every per-loan Cache API cache. */
export const BOOK_CACHE_PREFIX = 'nr-book:v1:';

const FILES_PATH = '/nubereader/file/';
const PLUGIN_QUERY_KEY = 'f';
const PLUGIN_QUERY_VALUE = 'plugin';
/** Tolerance before a clock that went backwards is treated as tampering (timezone/NTP drift). */
const CLOCK_ROLLBACK_TOLERANCE_MS = 300_000;

export interface LoanDates {
  loanEnd: number;
  lastSeenAt: number;
}

/**
 * NR2 serves every book from the same origin, so anything stored offline is keyed by loan:
 * the same book borrowed by two users of the same browser must not share data.
 */
export function loanKeyOf(
  metadata: Pick<MetadataResponse, 'otkId' | 'bookId' | 'userData'>,
): string {
  return `${metadata.otkId}:${metadata.bookId}:${metadata.userData.userId}`;
}

export function bookCacheName(loanKey: string): string {
  return `${BOOK_CACHE_PREFIX}${loanKey}`;
}

/** `metadata.files` entries are zip paths, relative to this prefix. */
export function bookFilesPrefix(metadata: Pick<MetadataResponse, 'otkId' | 'bookId'>): string {
  return `${FILES_PATH}${metadata.otkId}/${metadata.bookId}/`;
}

/**
 * Book-scoped files root (`/nubereader/file/{otkId}/{bookId}/{reldir}`). The server strips the
 * `{otkId}/{bookId}/` prefix, but having it in the URL makes it unique per book: books that share
 * an internal path (`OEBPS/Images/cover.jpg`) no longer collide in the HTTP cache.
 */
export function bookFilesRootUrl(
  metadata: Pick<MetadataResponse, 'otkId' | 'bookId' | 'readium'>,
): string {
  const { filesRootUrl } = metadata.readium;
  // Leave unknown layouts (other hosts, mocks) untouched.
  if (!filesRootUrl.startsWith(FILES_PATH)) return filesRootUrl;
  return `${bookFilesPrefix(metadata)}${filesRootUrl.slice(FILES_PATH.length)}`;
}

/**
 * URL stored in the browser extension. It must not contain a `?`: the extension appends
 * `?f=plugin` (or `&f=plugin` if there is already one) and the hash router has to read it as a
 * query param of the reader route.
 */
export function offlineBookmarkUrl(
  location: Pick<Location, 'origin' | 'pathname'>,
  token: string,
  cipherKey: string,
): string {
  return `${location.origin}${location.pathname}#/${token}/${cipherKey}`;
}

/** The extension opens offline books with `?f=plugin` inside the hash. */
export function isOpenedFromExtension(hash: string): boolean {
  const query = hash.split('?')[1] ?? '';
  return new URLSearchParams(query).get(PLUGIN_QUERY_KEY) === PLUGIN_QUERY_VALUE;
}

/**
 * Absolute end of the loan. Prefers the server value; otherwise approximates it like NR1 did
 * (`deadline` is the loan length in ms counted from the token creation).
 */
export function loanEndOf(
  metadata: Pick<MetadataResponse, 'deadline' | 'loanEndDate'>,
  downloadedAt: number,
): number {
  const serverEnd = metadata.loanEndDate ? Date.parse(metadata.loanEndDate) : Number.NaN;
  if (Number.isFinite(serverEnd)) return serverEnd;
  const deadline = Number(metadata.deadline);
  return Number.isFinite(deadline) && deadline > 0
    ? downloadedAt + deadline
    : Number.POSITIVE_INFINITY;
}

export function isLoanExpired(dates: LoanDates, now: number): boolean {
  return now > dates.loanEnd;
}

/** Someone set the device clock back to stretch an expired loan. */
export function isClockRolledBack(dates: LoanDates, now: number): boolean {
  return now < dates.lastSeenAt - CLOCK_ROLLBACK_TOLERANCE_MS;
}
